import { useEffect, useState } from "react";
import { ListChecks, CheckCircle2, Clock, AlertTriangle, Gauge } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const KPI = ({ icon: Icon, label, value, cls }) => (
  <div data-testid={`task-kpi-${label}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
    <Icon className={`h-5 w-5 ${cls}`} />
    <p className="mt-2 text-2xl font-semibold text-[#153D35]">{value}</p>
    <p className="text-xs text-[#68756F]">{label}</p>
  </div>
);

function ShiftCard({ s }) {
  return (
    <div data-testid={`task-shift-${s.shift_id}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
      <div className="flex items-center justify-between"><p className="font-semibold text-[#153D35]">{s.shift_name}</p><span className="text-lg font-semibold text-[#2563EB]">{s.compliance}%</span></div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-[#2563EB]" style={{ width: `${s.compliance}%` }} /></div>
      <p className="mt-2 text-xs text-[#68756F]">Realizadas: {s.done} · Pendientes: {s.pending} · Atrasadas: {s.late}</p>
    </div>
  );
}

export function TaskDashboard() {
  const { activeLocation } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    const q = activeLocation ? `?location_id=${activeLocation}` : "";
    api.get(`/tasks/dashboard${q}`).then(({ data }) => setD(data)).catch(() => setD(null));
  }, [activeLocation]);
  if (!d) return null;
  const k = d.kpis;
  return (
    <div className="mb-8">
      <h2 className="mb-1 flex items-center gap-2 font-display text-xl font-semibold text-[#153D35]"><ListChecks className="h-5 w-5 text-[#2563EB]" /> Tareas de hoy</h2>
      <p className="mb-4 text-sm text-[#68756F]">¿Mis locales están al día con sus tareas?</p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KPI icon={CheckCircle2} label="Realizadas" value={k.done} cls="text-emerald-600" />
        <KPI icon={Clock} label="Pendientes" value={k.pending} cls="text-amber-600" />
        <KPI icon={AlertTriangle} label="Atrasadas" value={k.late} cls="text-red-600" />
        <KPI icon={Gauge} label="Cumplimiento" value={`${k.compliance}%`} cls="text-[#2563EB]" />
      </div>
      {d.by_shift?.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Cumplimiento por turno</p>
          <div className="grid gap-3 sm:grid-cols-2">{d.by_shift.map((s) => <ShiftCard key={s.shift_id} s={s} />)}</div>
        </div>
      )}
      {d.per_location?.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Cumplimiento por local</p>
          <div className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-[#F8FAFC] text-left text-xs text-[#68756F]"><th className="p-3">Local</th>{(d.shifts || []).map((s) => <th key={s.id} className="p-3">{s.name}</th>)}<th className="p-3">Total</th></tr></thead>
              <tbody>
                {d.per_location.map((l) => (
                  <tr key={l.location_id} data-testid={`task-loc-${l.location_id}`} className="border-b last:border-0">
                    <td className="p-3 font-medium text-[#153D35]">{l.location_name}</td>
                    {(d.shifts || []).map((s) => { const ps = l.per_shift.find((x) => x.shift_id === s.id); return <td key={s.id} className="p-3">{ps ? `${ps.compliance}%` : "—"}</td>; })}
                    <td className="p-3 font-semibold text-[#2563EB]">{l.compliance}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
